import {ControlMode} from '.'
import type Controls from '../controls'

type KeyboardEventHandlers = {
  onUse?: () => void
  onRelease?: () => void
}

const TRANSLATE_INCREMENT = 10
const ZOOM_INCREMENT = 0.5

// This mode handles controlling the rotation, zoom, and translation with the keyboard.
// Since these controls should take priority, the methods `onUse` and `onRelease`
// are called on instances of this class when a user takes control.
class KeyboardMode extends ControlMode {
  onUse: () => void
  onRelease: () => void

  shift = false
  modifier = false

  constructor(controls: Controls, active?: boolean, eventHandlers?: KeyboardEventHandlers) {
    super(controls, active)

    const {onUse, onRelease} = eventHandlers ?? {}

    this.onUse =
      onUse ??
      function () {
        return void 0
      }
    this.onRelease =
      onRelease ??
      function () {
        return void 0
      }
  }

  init() {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      this.keyDown(event)
    })
    window.addEventListener('keyup', (event: KeyboardEvent) => {
      this.keyUp(event)
    })
  }

  keyDown = (event: KeyboardEvent) => {
    if (!this.active) {
      this.onUse?.()
    }

    // Adjust viewport only when within viewport i.e. allow user to switch modes in the bar
    if (document.activeElement?.className !== 'render-viewport') return

    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', '-', '='].includes(event.key)) {
      event.preventDefault()
    }

    switch (event.key) {
      case 'ArrowUp':
        if (this.shift) {
          this.controls.translateDown(TRANSLATE_INCREMENT)
        } else {
          this.controls.orbitUp(this.controls.orbitIncrement)
        }
        break
      case 'ArrowDown':
        if (this.shift) {
          this.controls.translateUp(TRANSLATE_INCREMENT)
        } else {
          this.controls.orbitDown(this.controls.orbitIncrement)
        }
        break
      case 'ArrowLeft':
        if (this.shift) {
          this.controls.translateRight(TRANSLATE_INCREMENT)
        } else {
          this.controls.orbitLeft(this.controls.orbitIncrement)
        }
        break
      case 'ArrowRight':
        if (this.shift) {
          this.controls.translateLeft(TRANSLATE_INCREMENT)
        } else {
          this.controls.orbitRight(this.controls.orbitIncrement)
        }
        break
      // Mimics File Explorer zoom keyboard navigation
      case '=':
        if (this.modifier) this.controls.changeZoom(-ZOOM_INCREMENT)
        break
      case '-':
        if (this.modifier) this.controls.changeZoom(ZOOM_INCREMENT)
        break
      case 'Control':
      case 'Meta':
        this.modifier = true
        break
      case 'Shift':
        this.shift = true
        break
      default:
        break
    }
  }

  keyUp = (event: KeyboardEvent) => {
    if (this.active) {
      this.onRelease()
    }
    switch (event.key) {
      case 'Meta':
      case 'Control':
        this.modifier = false
        break
      case 'Shift':
        this.shift = false
        break
      default:
        break
    }
  }
}

export default KeyboardMode
